import {
  registerComponent,
  asyncRequest,
  formatMoney,
  handleize,
} from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "bulk-order-form",
  class BulkOrderForm extends WYATTElement {
    SECTIONS_TO_RENDER = [
      {
        name: "cart-count",
        liveRegion: "cart-count",
      },
      {
        name: "bulk-summary",
        liveRegion: "bulk-summary",
      },
      {
        name: "cart",
        liveRegion: "cart-items",
      },
      {
        name: "cart",
        liveRegion: "cart-totals",
      },
      {
        name: "cart",
        liveRegion: "cart-checkout",
      },
      {
        name: "cart",
        liveRegion: "cart-recommendations",
      },
    ];

    #timeout;
    #postedSuccessfully = false;
    #errors = [];

    constructor() {
      super();
      this.form = this.querySelector("form");
      this.formId = null;
      this.formStatus = this.querySelector("[data-form-status]");
      this.formSubmit = this.querySelector("[data-form-submit]");
      this.summary = this.closest("[data-summary]");
      this.setupCharge = Number(this.config.setupCharge) || 0;
      this.statusTimeout = 3000;
      this.moneyFormat = WYATT.shopify.moneyFormat;

      this.subscribe(
        WYATT.PubSub.events.updateQuantity,
        this.handleQuantityUpdate.bind(this),
      );

      this.boundedSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      if (this.form) {
        this.formId = this.form.getAttribute("id");
        this.form.addEventListener("submit", this.boundedSubmit);
      }
    }

    handleQuantityUpdate(event) {
      const { formId, quantity } = event.detail;

      if (formId !== this.formId) return;
      const newPrice = this.calculateTotalPrice(quantity);
      this.updatePrice(newPrice);
    }

    calculateTotalPrice(quantity) {
      let price =
        this.config.currentPrice * quantity || this.config.currentPrice;
      if (this.setupCharge) {
        price += this.setupCharge;
      }
      return price;
    }

    isBlankSelection() {
      const { blankOptions, blankValues, productId } = this.config;
      const optionTypes = blankOptions.map((option) => handleize(option));
      const optionValues = blankValues.map((value) => handleize(value));
      const summary = this.closest(`[data-summary="${productId}"]`);
      const selectors = summary.querySelectorAll("[data-option-selector]");

      return Array.from(selectors).some((select) => {
        const optionType = handleize(
          select.labels?.[0]?.textContent.trim() ?? "",
        );
        if (!optionTypes.some((type) => optionType.includes(type))) {
          return false;
        }

        const selectedOption = select.selectedOptions[0];
        const optionValue = handleize(selectedOption?.dataset.name ?? "");
        return optionValues.some((value) => optionValue.includes(value));
      });
    }

    updatePrice(newPrice) {
      const totalPriceSelectors =
        this.summary.querySelectorAll("[data-total-price]");
      const formattedPrice = formatMoney(newPrice, this.moneyFormat);
      totalPriceSelectors.forEach((element) => {
        element.textContent = formattedPrice;
      });
    }

    getSectionNames() {
      return [
        ...new Set(this.SECTIONS_TO_RENDER.map((section) => section.name)),
      ];
    }

    async cartSetupFees(id) {
      try {
        const { items = [] } = await asyncRequest(
          `${WYATT.routes.cart.url}.js`,
          {},
          { reponseFormat: "json" },
        );
        return items.some(
          // eslint-disable-next-line no-underscore-dangle
          (item) => item.properties?._parent_item === String(id),
        );
      } catch (error) {
        return false;
      }
    }

    async addToCart() {
      try {
        const formData = new FormData(this.form);
        const id = formData.get("id");
        const quantity = formData.get("quantity");
        const isBundle = formData.get("bundle-item") || false;
        const properties = {};
        const propertiesRegex = /properties\[(.*?)\]/;

        Array.from(formData).forEach(([name, value]) => {
          const propertiesMatch = propertiesRegex.exec(name);
          if (propertiesMatch && propertiesMatch.length > 1) {
            properties[propertiesMatch[1]] = value;
          }
        });

        const item = {
          id,
          quantity,
          properties,
        };

        const items = [item];

        const { setupChargeId } = this.config;

        const isBlank = this.isBlankSelection();
        if (setupChargeId && !isBlank) {
          const setupCharge = {
            id: setupChargeId,
            quantity: 1,
            properties: {},
          };

          if (isBundle) {
            const hasSetupCharge = await this.cartSetupFees(id);
            if (!hasSetupCharge) {
              // eslint-disable-next-line dot-notation
              setupCharge.properties["_parent_item"] = id;
              items.push(setupCharge);
            }
          } else {
            setupCharge.parent_id = Number(id);
            items.push(setupCharge);
          }
        }

        const payload = {
          items,
          attributes: {},
          sections: this.getSectionNames(),
        };

        const response = await asyncRequest(`${WYATT.routes.cart_add_url}.js`, {
          ...WYATT.config.fetch.post,
          ...{ body: JSON.stringify(payload) },
        });
        this.#postedSuccessfully = true;

        const { sections } = JSON.parse(response);
        if (sections) {
          this.updateSections(sections);
        }
        this.resetQuantity();
      } catch (error) {
        window.console.error(error);
      } finally {
        this.#updateStatus();
      }
    }

    #resetState() {
      this.#postedSuccessfully = false;
      window.clearTimeout(this.#timeout);
      this.#updateStatus();
    }

    handleSubmit(event) {
      event.preventDefault();
      this.#resetState();
      this.addToCart();
    }

    resetQuantity() {
      const formId = this.form.getAttribute("id");
      const quantitySelector = document.querySelector(
        `[form="${formId}"][name="quantity"]`,
      );
      if (quantitySelector) {
        quantitySelector.value = quantitySelector.min
          ? parseInt(quantitySelector.min, 10)
          : 0;
      }
    }

    #updateStatus() {
      if (this.#postedSuccessfully) {
        const { alertSuccessMessage, successMessage } = this.config;
        // Status
        this.formStatus.innerHTML = alertSuccessMessage;
        // Submit
        this.formSubmit.textContent = successMessage;
        this.formSubmit.disabled = true;
        this.formSubmit.classList.add("button--success");
      } else {
        // Reset Form
        const { defaultMessage } = this.config;
        // Status
        this.formStatus.innerHTML = "";
        // Submit
        this.formSubmit.textContent = defaultMessage;
        this.formSubmit.disabled = false;
        this.formSubmit.classList.remove("button--success");
      }

      if (this.#errors.length || this.#postedSuccessfully) {
        this.formStatus.setAttribute("aria-hidden", false);

        this.#timeout = window.setTimeout(
          this.#resetState.bind(this),
          this.statusTimeout,
        );
      } else {
        // Reset Form
        this.formStatus.setAttribute("aria-hidden", true);
      }
    }

    updateSections(data) {
      this.publish(
        WYATT.PubSub.events.updateDOM,
        this.SECTIONS_TO_RENDER.map(({ liveRegion, name }) => ({
          liveRegion,
          html: data[name],
        })),
      );
    }
  },
);
